/**
 * Partitions Service - Fetches child partitions of a declarative-partitioned
 * PostgreSQL table. Partitioning is a PostgreSQL-only concept here, so the SQL
 * comes straight from `pgsqlQueries` rather than the engine-agnostic selector.
 */

import { executeQueryV2 } from './queryExecutor';
import { createExplorerExecutionId } from './explorerQueryUtils';
import { pgsqlQueries } from './queries';

export interface PartitionInfo {
  /** The child partition's own relname. */
  name: string;
  /** The child partition's schema (usually the parent's, but may differ). */
  schema: string;
  /** Human-readable bound clause — `FOR VALUES …` or `DEFAULT` (may be empty). */
  bound: string;
}

function toStr(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

export function mapPartitionInfoRows(rows: Record<string, unknown>[]): PartitionInfo[] {
  return rows
    .map((row) => ({
      name: toStr(row.name),
      schema: toStr(row.schema_name),
      bound: toStr(row.partition_bound),
    }))
    .filter((p) => p.name.length > 0);
}

/**
 * Format a partition's display name — "<name>  <bound>" (bound omitted when
 * empty). Mirrors the index display-name pattern so the bound reads inline in
 * the tree without a dedicated render branch.
 */
export function formatPartitionDisplayName(partition: PartitionInfo): string {
  return partition.bound ? `${partition.name}  ${partition.bound}` : partition.name;
}

export async function getPartitions(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
): Promise<{ partitions: PartitionInfo[]; error?: string }> {
  try {
    const result = await executeQueryV2({
      connectionId,
      sql: pgsqlQueries.tablePartitions(schema, tableName),
      database,
      executionId: createExplorerExecutionId(connectionId),
    });

    if (!result.success) {
      const errorMsg = result.error?.message || 'Unknown error';
      console.error(`[PartitionsService] Error getting partitions: ${errorMsg}`);
      return { partitions: [], error: errorMsg };
    }

    const rows = result.resultSets?.[0]?.rows || [];
    return { partitions: mapPartitionInfoRows(rows) };
  } catch (error) {
    console.error('[PartitionsService] Error in getPartitions:', error);
    return {
      partitions: [],
      error: error instanceof Error ? error.message : 'Unknown error getting partitions',
    };
  }
}
